import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

const KEY = "zebo_consent";
const DENIED = {
  ad_storage: "denied",
  analytics_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
};
const GRANTED = {
  ad_storage: "granted",
  analytics_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
};

function applyConsent(state) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", state === "granted" ? GRANTED : DENIED);
  }
}

export default function ConsentBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(KEY);
      if (!stored) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const choose = (val) => {
    try { localStorage.setItem(KEY, val); } catch { /* ignore */ }
    applyConsent(val);
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="consent-banner"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-xl z-[55] bg-[#143D63] text-[#F9F8F6] shadow-2xl border border-[#F8C21C]/30"
        >
          <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-start gap-4">
            <Cookie className="text-[#F8C21C] shrink-0 mt-1" size={22} />
            <div className="flex-1 text-sm leading-relaxed">
              <p className="font-serif text-xl mb-1">Cookies &amp; privacy</p>
              <p className="text-[#F9F8F6]/80">
                We use cookies to improve your experience and measure ad performance (Google Analytics &amp; Ads). Choose to accept or reject non-essential cookies. Read our{" "}
                <a
                  href="/privacy"
                  data-testid="consent-privacy-link"
                  className="text-[#F8C21C] underline underline-offset-2 hover:text-[#F9F8F6]"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
            <div className="flex flex-col sm:flex-col gap-2 shrink-0 w-full sm:w-auto">
              <button
                data-testid="consent-accept-btn"
                onClick={() => choose("granted")}
                className="bg-[#F8C21C] text-[#143D63] px-5 py-2.5 text-sm font-bold hover:bg-[#F9F8F6] transition-colors"
              >
                Accept all
              </button>
              <button
                data-testid="consent-reject-btn"
                onClick={() => choose("denied")}
                className="border border-[#F9F8F6]/30 text-[#F9F8F6] px-5 py-2.5 text-sm font-bold hover:bg-[#F9F8F6]/10 transition-colors"
              >
                Reject non-essential
              </button>
            </div>
            <button
              data-testid="consent-close-btn"
              onClick={() => choose("denied")}
              aria-label="Close (rejects non-essential)"
              className="absolute top-2 right-2 text-[#F9F8F6]/60 hover:text-[#F8C21C] sm:hidden"
            >
              <X size={18} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Footer helper to re-open the banner (e.g., from a "Cookie preferences" link)
export function reopenConsent() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  window.location.reload();
}
